import { useEffect, useState } from 'react';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { LoadingButton } from '@mui/lab';
import Lightbox from 'react-image-lightbox';
import IconButton from '@mui/material/IconButton';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import Box from '@mui/material/Box';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import * as pdfjsLib from 'pdfjs-dist';
import Typography from '@mui/material/Typography';

pdfjsLib.GlobalWorkerOptions.workerSrc = 'pdf.worker.min.mjs';

const isPdf = (name: string) => name.split('.').reverse()[0] === 'pdf';

interface Props {
    label: string;
    fixedUrlDownload: string;
    subLabel?: string;
}

const FixedDocumentBoxItem = ({ label, fixedUrlDownload, subLabel }: Props) => {
    const [lightBoxState, setLightBoxState] = useState<boolean>(false);
    const [imageIndex, setImageIndex] = useState<number>(0);
    const [loadingPdf, setLoadingPdf] = useState<boolean>(false);
    const [pdf, setPdf] = useState<any>();
    const [images, setImages] = useState<any[]>([]);

    const getImages = async () => {
        const imagesList = [];
        const canvas = document.createElement('canvas');
        canvas.setAttribute('className', 'canv');

        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const viewport = page.getViewport({ scale: 2 });
            canvas.height = viewport.height;
            canvas.width = viewport.width;
            const render_context = {
                canvasContext: canvas.getContext('2d'),
                viewport: viewport,
            };
            await page.render(render_context).promise;
            let img = canvas.toDataURL('image/png');
            imagesList.push(img);
        }
        setImages(imagesList);
        setLoadingPdf(false);
    };

    useEffect(() => {
        if (pdf) {
            getImages();
        }
    }, [pdf]);

    useEffect(() => {
        if (isPdf(fixedUrlDownload)) {
            setLoadingPdf(true);
            pdfjsLib.getDocument(fixedUrlDownload).promise.then((pdfDoc_: any) => {
                setPdf(pdfDoc_);
            });
        } else {
            setImages([fixedUrlDownload]);
        }
    }, []);

    const handleLightBoxOpen = () => {
        setLightBoxState(true);
    };

    const handleLightBoxClose = () => {
        setImageIndex(0);
        setLightBoxState(false);
    };

    const handleDownload = () => {
        window.open(fixedUrlDownload, '_blank');
    };

    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, auto)',
                gap: 1,
                alignItems: 'center',
            }}
        >
            <LoadingButton
                variant='outlined'
                sx={{
                    display: 'grid',
                    gridTemplateRows: 'repeat(2, auto)',
                    gap: 1,
                    placeItems: 'center',
                    placeContent: 'center',
                    width: 150,
                    height: 120,
                    fontWeight: 700,
                    textAlign: 'center',
                    fontSize: 13,
                    '& .MuiButton-startIcon': {
                        m: 0,
                        '.MuiSvgIcon-root': {
                            fontSize: 30,
                        },
                    },
                    p: 1,
                }}
                onClick={handleLightBoxOpen}
                startIcon={<InsertDriveFileOutlinedIcon />}
                loading={loadingPdf}
            >
                <Box>
                    {label}

                    {subLabel && (
                        <Typography
                            color='primary'
                            sx={{
                                fontSize: 12,
                            }}
                        >
                            {subLabel}
                        </Typography>
                    )}
                </Box>
            </LoadingButton>

            {lightBoxState && !loadingPdf && (
                <Lightbox
                    mainSrc={images[imageIndex]}
                    nextSrc={images[(imageIndex + 1) % images.length]}
                    prevSrc={images[(imageIndex + images.length - 1) % images.length]}
                    onCloseRequest={handleLightBoxClose}
                    onMovePrevRequest={() => setImageIndex((imageIndex + images.length - 1) % images.length)}
                    onMoveNextRequest={() => setImageIndex((imageIndex + 1) % images.length)}
                    toolbarButtons={[
                        <IconButton key='download' style={{ color: 'white' }} onClick={handleDownload}>
                            <DownloadOutlinedIcon />
                        </IconButton>,
                    ]}
                />
            )}

            <Box
                sx={{
                    display: 'grid',
                    alignItems: 'center',
                    gap: 1,
                }}
            >
                <IconButton color='primary' onClick={handleDownload}>
                    <FileDownloadIcon />
                </IconButton>
            </Box>
        </Box>
    );
};

export default FixedDocumentBoxItem;
